import { FC, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import clsx from 'clsx';
import axios from 'axios';
import { useListView } from '../core/ListViewProvider';
import { useQueryResponse } from '../core/QueryResponseProvider';
import { FormattedMessage } from 'react-intl';
import { ResellerGroup } from '../../../reseller-group/resellers-groups-list/core/_models';
import { Service } from '../../../service/services-list/core/_models';
import { GroupPricing } from '../core/_models';
import { createGroupPricing, updateGroupPricing } from '../core/_requests';

// **Validation Schema**
const pricingSchema = Yup.object().shape({
  reseller_group_id: Yup.number().required('Reseller Group is required'),
  service_id: Yup.number().nullable(), // Nullable because service-specific pricing is optional
  markup_percentage: Yup.number().required('Markup Percentage is required').min(0, 'Must be positive'),
  fixed_fee: Yup.number().required('Fixed Fee is required').min(0, 'Must be positive'),
  use_markup: Yup.boolean(),
  use_fixed_fee: Yup.boolean(),
});

type Props = {
  isGroupPricingLoading: boolean;
  grouppricing: GroupPricing;
};

const GroupPricingEditModalForm: FC<Props> = ({ grouppricing, isGroupPricingLoading }) => {
  const { setItemIdForUpdate } = useListView();
  const { refetch } = useQueryResponse();

  const [resellerGroups, setResellerGroups] = useState<ResellerGroup[]>([]);
  const [services, setServices] = useState<Service[]>([]);

  const API_URL = process.env.REACT_APP_THEME_API_URL;

  useEffect(() => {
    axios.get(`${API_URL}/admin/reseller-groups`).then((response) => {
      if (response.data.success) {
        setResellerGroups(response.data.data.reseller_groups);
      }
    });

    axios.get(`${API_URL}/admin/services`).then((response) => {
      if (response.data.success) {
        setServices(response.data.data.services);
      }
    });
  }, [API_URL]);

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetch();
    }
    setItemIdForUpdate(undefined);
  };

  const formik = useFormik({
    initialValues: {
      id: grouppricing.id || '',
      reseller_group_id: grouppricing.reseller_group_id || '',
      service_id: grouppricing.service_id || '',
      markup_percentage: grouppricing.markup_percentage || 0,
      fixed_fee: grouppricing.fixed_fee || 0,
      use_markup: grouppricing.use_markup || false,
      use_fixed_fee: grouppricing.use_fixed_fee || false,
    },
    validationSchema: pricingSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      try {
        if (values.id) {
          await updateGroupPricing(values); // Update existing pricing record
        } else {
          await createGroupPricing(values); // Create new pricing record
        }
      } catch (error) {
        console.error('Error submitting form:', error);
      } finally {
        setSubmitting(false);
        cancel(true);
      }
    },
  });
  

  return (
    <>
      <form id="kt_modal_add_pricing_form" className="form" onSubmit={formik.handleSubmit} noValidate>
        <div className="d-flex flex-column scroll-y me-n7 pe-7" id="kt_modal_add_pricing_scroll">
          <div className="row">
            {/* Reseller Group */}
            <div className="col-lg-6 fv-row mb-7">
              <label className="required fw-bold fs-6 mb-2">
                <FormattedMessage id="PRICING.FORM.INPUT.RESELLERGROUP" defaultMessage="گروه فروشنده" />
              </label>
              <select
                {...formik.getFieldProps('reseller_group_id')}
                className={clsx('form-select form-control', {
                  'is-invalid': formik.touched.reseller_group_id && formik.errors.reseller_group_id,
                  'is-valid': formik.touched.reseller_group_id && !formik.errors.reseller_group_id,
                })}
                disabled={formik.isSubmitting || isGroupPricingLoading}
              >
                <option value="" disabled>
                  -- Select Reseller Group --
                </option>
                {resellerGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Service (Optional) */}
            <div className="col-lg-6 fv-row mb-7">
              <label className="fw-bold fs-6 mb-2">
                <FormattedMessage id="PRICING.FORM.INPUT.SERVICE" defaultMessage="انتخاب خدمات (اختیاری)" />
              </label>
              <select
                {...formik.getFieldProps('service_id')}
                className={clsx('form-select form-control', {
                  'is-invalid': formik.touched.service_id && formik.errors.service_id,
                  'is-valid': formik.touched.service_id && !formik.errors.service_id,
                })}
                disabled={formik.isSubmitting || isGroupPricingLoading}
              >
                <option value="" disabled>
                  -- Select Service --
                </option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.service_category?.category_name} - {service.company?.company_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Markup Percentage */}
            <div className="col-lg-6 fv-row mb-7">
              <label className="required fw-bold fs-6 mb-2">
                <FormattedMessage id="PRICING.FORM.INPUT.MARKUP" defaultMessage="مفاد به اساس فیصدی (%)" />
              </label>
              <input
                {...formik.getFieldProps('markup_percentage')}
                className="form-control form-control-solid"
                type="number"
                autoComplete="off"
                disabled={formik.isSubmitting || isGroupPricingLoading}
              />
            </div>

            {/* Fixed Fee */}
            <div className="col-lg-6 fv-row mb-7">
              <label className="required fw-bold fs-6 mb-2">
                <FormattedMessage id="PRICING.FORM.INPUT.FIXEDFEE" defaultMessage="مفاد به اساس قیمت مشخص (AFN)" />
              </label>
              <input
                {...formik.getFieldProps('fixed_fee')}
                className="form-control form-control-solid"
                type="number"
                autoComplete="off"
                disabled={formik.isSubmitting || isGroupPricingLoading}
              />
            </div>

            {/* Use Markup Checkbox */}
            <div className="col-lg-6 fv-row mb-7">
              <label className="fw-bold fs-6 mb-2">
                <FormattedMessage id="PRICING.FORM.INPUT.USEMARKUP" defaultMessage="به اساس فیصدی انتخاب شود?" />
              </label>
              <input
                type="checkbox"
                {...formik.getFieldProps('use_markup')}
                checked={formik.values.use_markup}
                className="form-check-input"
              />
            </div>

            {/* Use Fixed Fee Checkbox */}
            <div className="col-lg-6 fv-row mb-7">
              <label className="fw-bold fs-6 mb-2">
                <FormattedMessage id="PRICING.FORM.INPUT.USEFIXEDFEE" defaultMessage="قیمت مشخص انتخاب شود?" />
              </label>
              <input
                type="checkbox"
                {...formik.getFieldProps('use_fixed_fee')}
                checked={formik.values.use_fixed_fee}
                className="form-check-input"
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="text-center pt-15">
          <button type="reset" onClick={() => cancel()} className="btn btn-light me-3">
            <FormattedMessage id="FORM.GENERAL.DISCARD" defaultMessage="Discard" />
          </button>
          <button type="submit" className="btn btn-primary" disabled={isGroupPricingLoading || formik.isSubmitting || !formik.isValid}>
            <FormattedMessage id="FORM.GENERAL.SUBMIT" defaultMessage="Submit" />
          </button>
        </div>
      </form>
    </>
  );
};

export { GroupPricingEditModalForm };
